import {
  EMOJI_STAMP_KINDS,
  SHAPE_STROKE_WIDTH_MAX_PX,
  isAvatarSnapshotDataUrl,
  isValidTableCellInlineStyles,
  isValidTableCells,
  isValidTableColumnFractions,
  isValidTableStorageSize,
  type CanvasObject,
  type ImageCanvasObject,
} from "@refinement-canvas/domain";
import {
  compareForClipboardOrder,
  objectToClipboardParts,
} from "./canvas-object-clipboard-text";

export const CANVAS_CLIPBOARD_MIME = "application/x-refinement-canvas-objects";
const FALLBACK_PREFIX = "refinement-canvas-objects:";

interface ClipboardPayload {
  readonly version: 1;
  readonly objects: readonly CanvasObject[];
}

// クリップボードへ載せる JSON ペイロード（カスタム MIME 用の生の文字列）。
function serializeClipboardPayload(objects: readonly CanvasObject[]): string {
  return JSON.stringify({ version: 1, objects } satisfies ClipboardPayload);
}

/**
 * キャンバスオブジェクトを text/plain 用の文字列（FALLBACK_PREFIX 付き JSON）へ直列化する。
 * DataTransfer 経路（writeCanvasObjectsToClipboardData）と非同期 Clipboard API 経路
 * （writeCanvasObjectsToClipboard）で同じ文字列を使い、読み取り側の readCanvasObjectsFromText が
 * どちらの経路でも同じように復元できるようにする。
 */
export function serializeCanvasObjectsToText(objects: readonly CanvasObject[]): string {
  return `${FALLBACK_PREFIX}${serializeClipboardPayload(objects)}`;
}

/** UTF-8 文字列を、属性値へ安全に入れられる Base64 へ変換する。 */
function encodeUtf8Base64(value: string): string {
  const bytes = new TextEncoder().encode(value);
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary);
}

/** Base64 を UTF-8 へ復号する。不正な UTF-8 は呼び出し側の try/catch で拒否する。 */
function decodeUtf8Base64(value: string): string {
  const binary = atob(value);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) bytes[index] = binary.charCodeAt(index);
  return new TextDecoder("utf-8", { fatal: true }).decode(bytes);
}

/**
 * 選択した図形の、人が読めるクリップボード内容（text/plain と text/html）を作る。
 *
 * - `plain` は各図形の Markdown 断片を「表示位置の昇順」で `\n\n` 連結したもの。
 *   出力できる種別が 1 つも無い選択（ペン・図形だけなど）では空文字になる。
 * - `html` のルート要素は常に `<div data-refinement-canvas-objects="…">` で、属性には JSON
 *   ペイロードを Base64 で持つ。ペイロードは並べ替え前の順序のまま、復元側が受理する種別だけ
 *   （`restorableObjects`）へ絞ったものである。キャンバス内の貼り付けはこの属性から復元するため、
 *   `plain` / `html` の見た目を変えても復元結果は変わらない。
 */
export function buildRichClipboardContent(objects: readonly CanvasObject[]): {
  readonly plain: string;
  readonly html: string;
} {
  const payloadBase64 = encodeUtf8Base64(
    serializeClipboardPayload(restorableObjects(objects))
  );
  const parts = [...objects].sort(compareForClipboardOrder).map(objectToClipboardParts);
  return {
    html:
      // payloadBase64 は Base64 文字列で `<` `>` `"` を含まず、連結する断片は
      // objectToClipboardParts が escapeHtml 済みの値だけで組み立てている。
      // nosemgrep: tmp.security-work.sast.rules.javascript.lang.security.html-in-template-string
      `<div data-refinement-canvas-objects="${payloadBase64}">` +
      parts.map((part) => part.html).join("") +
      `</div>`,
    plain: parts
      .map((part) => part.plain)
      .filter((plain) => plain.length > 0)
      .join("\n\n"),
  };
}

export function writeCanvasObjectsToClipboardData(
  clipboardData: DataTransfer,
  objects: readonly CanvasObject[]
): void {
  clipboardData.setData(
    CANVAS_CLIPBOARD_MIME,
    serializeClipboardPayload(restorableObjects(objects))
  );
  const rich = buildRichClipboardContent(objects);
  // text/plain は空文字になり得るが、項目自体は必ず設定する（省略すると text/html まで
  // 無視する貼り付け先があるため）。復元はカスタム MIME と text/html の data 属性が担う。
  clipboardData.setData("text/plain", rich.plain);
  clipboardData.setData("text/html", rich.html);
}

/**
 * コンテキストメニュー等、clipboard イベントの外からコピーを実行する。
 * ClipboardItem へ人が読める text/plain と、復元用の data 属性を持つ text/html を必ず入れ、
 * 単一画像のときだけ image/png も併記する。
 * ClipboardItem 非対応・write 拒否時は、text/plain しか残らないため従来の接頭辞付き
 * 形式（serializeCanvasObjectsToText）へ退避する。人間可読よりキャンバス内の復元を優先する
 * （ここで rich.plain を書くと、その環境ではキャンバスへ貼り付けられなくなる）。
 * 戻り値: "copied"=書き込んだ / "unavailable"=API 非対応・権限拒否・書き込み失敗。
 */
export async function writeCanvasObjectsToClipboard(
  objects: readonly CanvasObject[]
): Promise<"copied" | "unavailable"> {
  // 非セキュアコンテキストや古いブラウザでは navigator.clipboard 自体が無い。
  // 判定の形は use-paste-handler.ts の performClipboardPaste と揃える。
  if (typeof navigator === "undefined" || !navigator.clipboard) return "unavailable";
  const rich = buildRichClipboardContent(objects);
  if (typeof ClipboardItem !== "undefined" && navigator.clipboard.write) {
    const write = navigator.clipboard.write.bind(navigator.clipboard);
    const buildPayload = (png: Promise<Blob> | null): Record<string, Blob | Promise<Blob>> => ({
      "text/html": new Blob([rich.html], { type: "text/html" }),
      "text/plain": new Blob([rich.plain], { type: "text/plain" }),
      ...(png === null ? {} : { "image/png": png }),
    });
    // PNG の取得は await せず Promise のまま ClipboardItem へ渡し、write 自体は利用者の操作直後に
    // 始める。await してから write を呼ぶと、操作由来の許可が切れて拒否される環境がある。
    const image = singleImageForPng(objects);
    const png = image === null ? null : buildPngBlob(image.src);
    // 失敗しても未処理の拒否として残さない（下の PNG 無しの再試行で退避する）。
    png?.catch(() => undefined);
    try {
      await write([new ClipboardItem(buildPayload(png))]);
      return "copied";
    } catch {
      // 画像の取得に失敗した場合と、PNG を含む書き込みだけが拒否される環境のどちらでも、
      // PNG 無しで 1 回だけ試し直す。画像を併記できないことで、リッチな貼り付け（text/html）
      // まで失わないようにする。
      if (png !== null) {
        try {
          await write([new ClipboardItem(buildPayload(null))]);
          return "copied";
        } catch {
          // 2 回とも拒否されたら、下の接頭辞付き退避で復元だけを守る。
        }
      }
    }
  }
  try {
    await navigator.clipboard.writeText(
      serializeCanvasObjectsToText(restorableObjects(objects))
    );
    return "copied";
  } catch {
    // 権限拒否・Document is not focused・writeText 未実装（TypeError）をまとめて握る。
    return "unavailable";
  }
}

/**
 * クリップボードへ載せてよいオブジェクトだけを残す（貼り付け側が受理する種別と同じ判定）。
 *
 * `text/html` の data 属性と、退避経路の接頭辞付き `text/plain` は、いずれも他アプリの
 * クリップボードへそのまま渡る。復元にも使われない `comment-thread` の会話本文が、復号すれば
 * 読める形で外部へ出ていくのを防ぐため、書き込み側でも同じ検証を通す。
 * 復元側（`parsePayload`）は元から同じ検証で捨てているため、貼り付け結果は変わらない。
 */
function restorableObjects(objects: readonly CanvasObject[]): readonly CanvasObject[] {
  return objects.filter(isCanvasObject);
}

/**
 * PNG を併記する対象（公開 URL を持つ画像 1 件だけの選択）なら、その図形を返す。
 * 判定を同期で済ませ、ClipboardItem の組み立てを待たせないための関数。
 */
function singleImageForPng(objects: readonly CanvasObject[]): ImageCanvasObject | null {
  const only = objects.length === 1 ? objects[0] : undefined;
  if (only === undefined || only.type !== "image" || !isHttpUrl(only.src)) return null;
  return only;
}

/**
 * 画像の添付を PNG の Blob として取得する（取得・変換できなければ reject する）。
 * 呼び出し側は、この Promise をそのまま ClipboardItem へ渡し、失敗したら PNG 無しで書き直す。
 * Ctrl+C / Ctrl+X 経路では使わない（copy イベントのハンドラは同期であり、await をまたぐと
 * event.clipboardData を使えなくなるため）。
 */
async function buildPngBlob(src: string): Promise<Blob> {
  const response = await fetch(src);
  if (!response.ok) throw new Error(`画像を取得できませんでした（HTTP ${String(response.status)}）`);
  const blob = await response.blob();
  if (blob.type === "image/png") return blob;
  // jpeg / gif / webp は PNG へ変換する。変換手段が無い環境では併記を諦める。
  if (typeof createImageBitmap !== "function" || typeof OffscreenCanvas === "undefined") {
    throw new Error("この環境では PNG へ変換できません");
  }
  const bitmap = await createImageBitmap(blob);
  try {
    const canvas = new OffscreenCanvas(bitmap.width, bitmap.height);
    const context = canvas.getContext("2d");
    if (context === null) throw new Error("2D コンテキストを取得できませんでした");
    context.drawImage(bitmap, 0, 0);
    return await canvas.convertToBlob({ type: "image/png" });
  } finally {
    // ImageBitmap は GC 任せにすると解放が遅れるため、成功・失敗のどちらでも明示的に閉じる。
    bitmap.close();
  }
}

export function readCanvasObjectsFromClipboardData(
  clipboardData: DataTransfer
): readonly CanvasObject[] {
  const custom = clipboardData.getData(CANVAS_CLIPBOARD_MIME);
  if (custom) {
    const objects = parsePayload(custom);
    if (objects.length > 0) return objects;
  }
  const htmlObjects = readCanvasObjectsFromHtml(clipboardData.getData("text/html"));
  if (htmlObjects.length > 0) return htmlObjects;
  return readCanvasObjectsFromText(clipboardData.getData("text/plain"));
}

const EMBEDDED_OBJECTS_ATTRIBUTE_RE = /\sdata-refinement-canvas-objects="([A-Za-z0-9+/=]+)"/u;

/** text/html の埋め込み属性だけを抽出し、HTML 要素を生成せずにオブジェクトを復元する。 */
export function readCanvasObjectsFromHtml(html: string): readonly CanvasObject[] {
  const matched = EMBEDDED_OBJECTS_ATTRIBUTE_RE.exec(html);
  if (!matched?.[1]) return [];
  try {
    return parsePayload(decodeUtf8Base64(matched[1]));
  } catch {
    return [];
  }
}

/**
 * text/plain 文字列からキャンバスオブジェクトを復元する。
 * 非同期 Clipboard API（navigator.clipboard.readText / read）経由の貼り付けは、カスタム MIME を
 * 安定して取得できない環境があるため、書き込み時に併記した text/plain フォールバック（FALLBACK_PREFIX
 * 付き）から復元する。DataTransfer 経路（readCanvasObjectsFromClipboardData）と同じパーサ・検証を通す。
 */
export function readCanvasObjectsFromText(text: string): readonly CanvasObject[] {
  if (text.startsWith(FALLBACK_PREFIX)) {
    return parsePayload(text.slice(FALLBACK_PREFIX.length));
  }
  return [];
}

function parsePayload(raw: string): readonly CanvasObject[] {
  try {
    const parsed = JSON.parse(raw) as Partial<ClipboardPayload>;
    if (parsed.version !== 1 || !Array.isArray(parsed.objects)) return [];
    return parsed.objects.filter(isCanvasObject);
  } catch {
    return [];
  }
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

function isOptionalFiniteNumber(value: unknown): value is number | undefined {
  return value === undefined || isFiniteNumber(value);
}

// 表の構造 revision は非負の安全な整数（サーバ採番値）。小数・負数・巨大値は受理しない。
function isOptionalStructureRevision(value: unknown): value is number | undefined {
  return value === undefined || (typeof value === "number" && Number.isSafeInteger(value) && value >= 0);
}

// 図形線幅はサーバの永続・op スキーマと同じ 1〜32 の整数だけを受理する。
function isOptionalShapeStrokeWidth(value: unknown): value is number | undefined {
  return (
    value === undefined ||
    (typeof value === "number" &&
      Number.isInteger(value) &&
      value >= 1 &&
      value <= SHAPE_STROKE_WIDTH_MAX_PX)
  );
}

function isOptionalString(value: unknown): value is string | undefined {
  return value === undefined || typeof value === "string";
}

// text / sticky の文字寄せ用の検証。サーバ側 zod（textAlign: enum(left/center/right)）と同等の
// 制約をクライアント貼り付け経路でも適用し、細工した貼り付けデータで不正値が一時的に
// ローカル状態へ残らないようにする（color / fontSize と同水準に揃える）。
function isOptionalTextAlign(value: unknown): boolean {
  return value === undefined || value === "left" || value === "center" || value === "right";
}

// shape / pen-stroke の線種。欠落は旧データ互換で許可し、未知値は外部入力として拒否する。
function isOptionalStrokePattern(value: unknown): boolean {
  return value === undefined || value === "solid" || value === "dotted";
}

// shape の矢印向き（arrowFromCorner）用の検証。サーバ側 zod（enum の 4 値）と同等の制約を
// クライアント貼り付け経路でも適用し、細工した貼り付けデータで不正値が一時的にローカル状態へ
// 残らないようにする。
function isOptionalArrowCorner(value: unknown): boolean {
  return (
    value === undefined ||
    value === "top-left" ||
    value === "top-right" ||
    value === "bottom-left" ||
    value === "bottom-right"
  );
}

// 軸そろえ矢印の起点辺。サーバ側 zod と同じ 4 値だけを受理する。
function isOptionalArrowEdge(value: unknown): boolean {
  return (
    value === undefined ||
    value === "left" ||
    value === "right" ||
    value === "top" ||
    value === "bottom"
  );
}

// image/video の placeholderSrc 用の検証。サーバ側 zod スキーマ（apps/api/src/lib/canvas-object-schema.ts
// の PlaceholderSrcSchema）と同等の制約をクライアント貼り付け経路でも適用する。
// クリップボードは外部入力（リポジトリ外の text/plain fallback を含む）であり、サーバ reject 前の
// 楽観更新後に <img src> へ流れて任意 data: URL（特に SVG）を描画候補にしてしまう余地を塞ぐ。
function isOptionalPlaceholderSrc(value: unknown): value is string | undefined {
  return (
    value === undefined ||
    (typeof value === "string" &&
      value.length <= 8192 &&
      /^data:image\/(jpeg|jpg|png|gif|webp);/i.test(value))
  );
}

// image / video の本体 src 用の検証。サーバ側 zod（SafeAttachmentSrcSchema）と同等に、
// http(s) / blob: / ラスタ画像の data: URL のみ許可する。クリップボード（外部入力）から、
// サーバ reject 前の楽観描画で data:image/svg+xml や javascript: などが <img src> へ流れる
// 余地を塞ぎ、placeholderSrc / link-card url と検証方針を揃える。
function isSafeAttachmentSrc(value: unknown): value is string {
  return (
    typeof value === "string" &&
    value.length <= 8192 &&
    (/^https?:\/\//i.test(value) ||
      value.startsWith("blob:") ||
      /^data:image\/(jpeg|jpg|png|gif|webp)[;,]/i.test(value))
  );
}

// link-card の url 用のスキーム検証。サーバ側 zod スキーマ（HttpUrlSchema）と同等に http / https
// のみ許可する。クリップボード（text/plain fallback を含む外部入力）から、楽観更新前に
// javascript: / data: 等の危険スキームを持つ link-card を弾く。
// imageUrl は <img src>（非実行）専用で危険スキームでも XSS にならず、サーバ側も緩く受理するため
// ここでも url のみ厳格化し、imageUrl は文字列であれば許容する（描画/サーバ判定の食い違いを避ける）。
function isHttpUrl(value: unknown): value is string {
  if (typeof value !== "string" || value.length > 8192) return false;
  try {
    const protocol = new URL(value).protocol;
    return protocol === "http:" || protocol === "https:";
  } catch {
    return false;
  }
}

function hasBaseFields(value: unknown): value is Record<string, unknown> {
  if (typeof value !== "object" || value === null) return false;
  const record = value as Record<string, unknown>;
  return (
    typeof record.id === "string" &&
    isFiniteNumber(record.x) &&
    isFiniteNumber(record.y) &&
    isFiniteNumber(record.w) &&
    isFiniteNumber(record.h) &&
    isOptionalFiniteNumber(record.rotation) &&
    isFiniteNumber(record.zOrder) &&
    isFiniteNumber(record.version) &&
    typeof record.createdAt === "string" &&
    typeof record.createdBy === "string" &&
    typeof record.updatedAt === "string" &&
    typeof record.updatedBy === "string"
  );
}

function isCanvasObject(value: unknown): value is CanvasObject {
  if (!hasBaseFields(value)) return false;
  // クリップボード文字列は外部入力なので、種別ごとの必須フィールドまで
  // 確認してからキャンバスオブジェクトとして扱う。
  switch (value.type) {
    case "sticky":
      return (
        typeof value.text === "string" &&
        isOptionalString(value.color) &&
        // 細工した貼り付けデータで updatedByName にオブジェクトが入り込まないよう型を検証する。
        // sticky-object.tsx ではこの値を React 子要素として直接描画するため、文字列以外が
        // 流入すると貼り付け直後に描画エラー（不正な ReactNode）で画面が壊れる。
        isOptionalString(value.updatedByName) &&
        isOptionalTextAlign(value.textAlign)
      );
    case "text":
      return (
        typeof value.text === "string" &&
        isOptionalString(value.color) &&
        isOptionalString(value.fill) &&
        isOptionalFiniteNumber(value.fontSize) &&
        (value.bold === undefined || typeof value.bold === "boolean") &&
        isOptionalTextAlign(value.textAlign)
      );
    case "table":
      return (
        isValidTableCells(value.cells) &&
        (value.columnFractions === undefined ||
          isValidTableColumnFractions(value.columnFractions, value.cells[0]?.length ?? 0)) &&
        // セルの部分書式も、保存時と同じ正規形・同じ保存サイズ上限で検証する。
        // 検証しないと、細工した貼り付けデータの不正な run が楽観適用と描画へ流入し、
        // サーバが作成を拒否するまでのあいだクライアントだけが不正な状態を持つ。
        (value.cellInlineStyles === undefined ||
          (isValidTableCellInlineStyles(value.cellInlineStyles, value.cells) &&
            isValidTableStorageSize(value.cells, value.cellInlineStyles))) &&
        // 構造 revision はサーバが採番する値。貼り付け元の値をそのまま持ち込ませないよう
        // 形だけを検証し、複製時は cloneObjectAsDraft が落とす。
        isOptionalStructureRevision(value.structureRevision) &&
        isOptionalString(value.color) &&
        isOptionalFiniteNumber(value.fontSize)
      );
    case "section":
      // セクション（領域コンテナ）。タイトル（text）は必須、背景色（fill）は任意。
      return typeof value.text === "string" && isOptionalString(value.fill);
    case "shape":
      return (
        (value.shapeKind === "rectangle" ||
          value.shapeKind === "circle" ||
          value.shapeKind === "arrow") &&
        isOptionalString(value.fill) &&
        isOptionalString(value.stroke) &&
        isOptionalStrokePattern(value.strokePattern) &&
        isOptionalShapeStrokeWidth(value.strokeWidth) &&
        isOptionalArrowCorner(value.arrowFromCorner) &&
        isOptionalArrowEdge(value.arrowFromEdge)
      );
    case "image":
      return (
        isSafeAttachmentSrc(value.src) &&
        isOptionalString(value.alt) &&
        isOptionalPlaceholderSrc(value.placeholderSrc)
      );
    case "video":
      return (
        isSafeAttachmentSrc(value.src) &&
        typeof value.mimeType === "string" &&
        isOptionalPlaceholderSrc(value.placeholderSrc)
      );
    case "link-card":
      return (
        isHttpUrl(value.url) &&
        typeof value.title === "string" &&
        isOptionalString(value.description) &&
        isOptionalString(value.imageUrl) &&
        isOptionalString(value.siteName)
      );
    case "pen-stroke":
      return (
        typeof value.color === "string" &&
        isFiniteNumber(value.thickness) &&
        isOptionalStrokePattern(value.strokePattern) &&
        Array.isArray(value.points) &&
        value.points.every(isStrokePoint)
      );
    case "stamp": {
      // 作成者名スナップショットは、細工した貼り付けデータでオブジェクトが入り込まないよう型を検証する
      // （stamp-object.tsx でバッジに React 子要素として描画するため、文字列以外の流入を防ぐ。
      //  付箋 updatedByName と同じ理由）。貼り付け→create ではサーバが貼った人の名前で上書きする。
      if (!isOptionalString(value.createdByName)) return false;
      // avatar スタンプは「安全なラスタ data:image/ スナップショット」の avatarSrc を必須とする。
      // 判定は domain の単一ソース isAvatarSnapshotDataUrl を参照し、サーバ ObjectSchema・選択判定と一致させる。
      // 絵文字スタンプは avatarSrc を持たない。
      if (value.stampKind === "avatar") {
        return isAvatarSnapshotDataUrl(value.avatarSrc);
      }
      return (
        typeof value.stampKind === "string" &&
        EMOJI_STAMP_KINDS.includes(value.stampKind as (typeof EMOJI_STAMP_KINDS)[number]) &&
        value.avatarSrc === undefined
      );
    }
    case "connector":
      return (
        isConnectorEndpoint(value.from) &&
        isConnectorEndpoint(value.to) &&
        isOptionalString(value.stroke) &&
        isOptionalShapeStrokeWidth(value.strokeWidth)
      );
    default:
      return false;
  }
}

// コネクタ端点（{ objectId, side }）の検証。クリップボード（外部入力）から不正な端点が
// 一時的にローカル状態へ流入しないよう、サーバ zod（ConnectorEndpointSchema）と同等に確認する。
function isConnectorEndpoint(value: unknown): boolean {
  if (typeof value !== "object" || value === null) return false;
  const record = value as Record<string, unknown>;
  return (
    typeof record.objectId === "string" &&
    (record.side === "top" ||
      record.side === "right" ||
      record.side === "bottom" ||
      record.side === "left")
  );
}

function isStrokePoint(value: unknown): value is { x: number; y: number; pressure: number } {
  if (typeof value !== "object" || value === null) return false;
  const record = value as Record<string, unknown>;
  return isFiniteNumber(record.x) && isFiniteNumber(record.y) && isFiniteNumber(record.pressure);
}
